import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  ExternalLink,
  MoreHorizontal,
  Building2,
  Filter
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PortfolioList = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFund, setSelectedFund] = useState('all');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [companiesRes, fundsRes] = await Promise.all([
        axios.get(`${API}/admin/portfolio?include_inactive=true`, { withCredentials: true }),
        axios.get(`${API}/admin/funds`, { withCredentials: true })
      ]);
      setCompanies(companiesRes.data);
      setFunds(fundsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      console.log('Attempting to delete company:', deleteId);
      const response = await axios.delete(`${API}/admin/portfolio/${deleteId}`, { withCredentials: true });
      console.log('Delete response:', response.data);
      toast.success('Company deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to delete company');
    } finally {
      setDeleteId(null);
    }
  };

  const getFundName = (fundId) => {
    const fund = funds.find(f => f.fund_id === fundId);
    return fund?.name || fundId;
  };

  const getCompanyFundIds = (company) => {
    if (Array.isArray(company.fund_ids) && company.fund_ids.length) return company.fund_ids;
    if (company.fund_id) return [company.fund_id];
    return [];
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.sectors?.some(s => s.toLowerCase().includes(searchTerm.toLowerCase()));
    const companyFundIds = getCompanyFundIds(company);
    const matchesFund = selectedFund === 'all' || companyFundIds.includes(selectedFund);
    return matchesSearch && matchesFund;
  });

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#211F1F]">Portfolio Companies</h1>
          <p className="text-gray-500">Manage your portfolio companies</p>
        </div>
        <Button
          onClick={() => navigate('/admin/portfolio/new')}
          className="bg-[#C8473A] hover:bg-[#B74F40]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Company
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search companies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={selectedFund} onValueChange={setSelectedFund}>
          <SelectTrigger className="w-full md:w-48">
            <SelectValue placeholder="Filter by Fund" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Funds</SelectItem>
            {funds.map(fund => (
              <SelectItem key={fund.fund_id} value={fund.fund_id}>
                {fund.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Sectors</TableHead>
              <TableHead>Funds</TableHead>
              <TableHead>Invested</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  Loading...
                </TableCell>
              </TableRow>
            ) : filteredCompanies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                  <p className="text-gray-500">No companies found</p>
                </TableCell>
              </TableRow>
            ) : (
              filteredCompanies.map(company => (
                <TableRow key={company.company_id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {company.logo_url ? (
                        <img
                          src={company.logo_url.startsWith('/') ? `${BACKEND_URL}${company.logo_url}` : company.logo_url}
                          alt={company.logo_alt || company.name}
                          className="w-10 h-10 rounded-lg object-contain bg-gray-50"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium">{company.name}</p>
                        {company.location && (
                          <p className="text-sm text-gray-500">{company.location}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {company.sectors?.slice(0, 2).map(sector => (
                        <Badge key={sector} variant="secondary" className="text-xs">
                          {sector}
                        </Badge>
                      ))}
                      {company.sectors?.length > 2 && (
                        <Badge variant="secondary" className="text-xs">
                          +{company.sectors.length - 2}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {getCompanyFundIds(company).map((fid) => (
                        <Badge key={fid} variant="secondary" className="text-xs">
                          {getFundName(fid)}
                        </Badge>
                      ))}
                      {getCompanyFundIds(company).length === 0 && (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {company.invested_month}/{company.invested_year}
                  </TableCell>
                  <TableCell>
                    <Badge variant={company.is_active ? 'default' : 'secondary'}>
                      {company.status || 'active'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/portfolio/${company.company_id}`)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        {company.website_url && (
                          <DropdownMenuItem asChild>
                            <a href={company.website_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Visit Website
                            </a>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => setDeleteId(company.company_id)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Company</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this company? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button 
              onClick={handleDelete} 
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PortfolioList;
